import React, {MouseEvent, useRef} from 'react';
import Icon from '@sharedComponents/Icon';
import {usePantameraService} from "@frontendUtils/pantamera-service";
import './Scss/input-checkbox.scss';

type IInputCheckboxProps = {
  id: string;
  className?: string;
  disabled?: boolean;
  label: any;
  hideLabel?: boolean;
  checked: boolean;
  onChange: any;
  disableClickLabel?: boolean;
};

const {getCssVariable} = usePantameraService();

const nativeInteractiveLabelSelector = [
  'a',
  'button',
  'input',
  'select',
  'textarea',
  'summary',
].join(',');

const customInteractiveLabelSelector = [
  '[role="button"]',
  '[role="link"]',
  '.button-link-block',
].join(',');

const hasClosestElementInLabel = (
  target: EventTarget | null,
  labelElement: HTMLLabelElement,
  selector: string
) => {
  if (!(target instanceof Element)) {
    return false;
  }

  const interactiveElement = target.closest(selector);

  return !!interactiveElement && labelElement.contains(interactiveElement);
};

const InputCheckbox = ({hideLabel = false,...props}: IInputCheckboxProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const clickHiddenCheckbox = () => {
    inputRef.current?.click();
  };

  const onClickLabel = (e: MouseEvent<HTMLLabelElement>) => {
    if (
      hasClosestElementInLabel(
        e.target,
        e.currentTarget,
        nativeInteractiveLabelSelector
      )
    ) {
      return;
    }

    e.preventDefault();

    if (
      hasClosestElementInLabel(
        e.target,
        e.currentTarget,
        customInteractiveLabelSelector
      )
    ) {
      return;
    }

    if (props.disableClickLabel) {
      return;
    }

    clickHiddenCheckbox();
  };

  const colorCheckmarkIcon = getCssVariable('color-checkmark-icon');

  return (
    <div className={`checkbox-wrapper ${props.className}`}>
      <input
        ref={inputRef}
        className={`checkbox-wrapper__hidden-checkbox`}
        type={'checkbox'}
        checked={props.checked}
        id={props.id}
        onChange={props.onChange}
        disabled={!!props.disabled}
      />
      <div
        onClick={clickHiddenCheckbox}
        className={`checkbox-wrapper__checkbox styled-checkbox ${
          props.disabled ? 'disabled' : ''
        } ${props.checked ? 'checkbox-wrapper__checkbox--checked' : ''}`}
      >
        <Icon
          iconName="check"
          color={colorCheckmarkIcon}
          size={11}
        />
      </div>
      <label
        className={`checkbox-wrapper__label ${hideLabel ? 'sr-only' : ''}`}
        htmlFor={props.id}
        onClick={onClickLabel}
      >
        {props.label}
      </label>
    </div>
  );
};

export default InputCheckbox;
